import { useMemo, useRef, useState } from 'react';
import { PanResponder, View } from 'react-native';
import Svg, { Circle, Line } from 'react-native-svg';
import * as Haptics from 'expo-haptics';
import { C, alpha } from './theme';

/** Grade 3x3 de desenho padrão. Ao soltar o dedo, entrega a sequência dos pontos ("0-4-8-5").
 *  Os pontos são numerados assim:  0 1 2 / 3 4 5 / 6 7 8. */
export function Padrao({ tamanho = 270, cor, erro, onFim }: { tamanho?: number; cor: string; erro?: boolean; onFim: (seq: string) => void }) {
  const passo = tamanho / 3;
  const raioToque = passo * 0.36;
  const centros = useMemo(() => Array.from({ length: 9 }, (_, i) => ({ x: (i % 3) * passo + passo / 2, y: Math.floor(i / 3) * passo + passo / 2 })), [passo]);
  const [seq, setSeq] = useState<number[]>([]);
  const [dedo, setDedo] = useState<{ x: number; y: number } | null>(null);
  const seqRef = useRef<number[]>([]);
  const fimRef = useRef(onFim); fimRef.current = onFim;

  const tocar = (x: number, y: number) => {
    setDedo({ x, y });
    const i = centros.findIndex(c => Math.hypot(c.x - x, c.y - y) < raioToque);
    if (i < 0 || seqRef.current.includes(i)) return;
    // passou por cima de um ponto do meio sem tocar nele: entra também (como no Android)
    const ult = seqRef.current[seqRef.current.length - 1];
    if (ult !== undefined) {
      const meio = (ult + i) / 2;
      const a = centros[ult], b = centros[i];
      if (Number.isInteger(meio) && Math.abs((a.x + b.x) / 2 - centros[meio].x) < 1 && Math.abs((a.y + b.y) / 2 - centros[meio].y) < 1 && !seqRef.current.includes(meio)) {
        seqRef.current = [...seqRef.current, meio];
      }
    }
    seqRef.current = [...seqRef.current, i];
    setSeq(seqRef.current);
    Haptics.selectionAsync().catch(() => {});
  };

  const pan = useMemo(() => PanResponder.create({
    onStartShouldSetPanResponder: () => true,
    onStartShouldSetPanResponderCapture: () => true,
    onMoveShouldSetPanResponder: () => true,
    onMoveShouldSetPanResponderCapture: () => true,
    onPanResponderTerminationRequest: () => false,
    onPanResponderGrant: e => { seqRef.current = []; setSeq([]); tocar(e.nativeEvent.locationX, e.nativeEvent.locationY); },
    onPanResponderMove: e => tocar(e.nativeEvent.locationX, e.nativeEvent.locationY),
    onPanResponderRelease: () => {
      const s = seqRef.current;
      setDedo(null);
      if (s.length) fimRef.current(s.join('-'));
      setTimeout(() => { seqRef.current = []; setSeq([]); }, 350);
    },
  }), [centros]); // eslint-disable-line react-hooks/exhaustive-deps

  const corLinha = erro ? C.danger : cor;
  const ult = seq.length ? centros[seq[seq.length - 1]] : null;
  return (
    <View {...pan.panHandlers} style={{ width: tamanho, height: tamanho, alignSelf: 'center' }}>
      <View pointerEvents="none" style={{ width: tamanho, height: tamanho }}>
      <Svg width={tamanho} height={tamanho}>
        {seq.slice(1).map((p, k) => (
          <Line key={k} x1={centros[seq[k]].x} y1={centros[seq[k]].y} x2={centros[p].x} y2={centros[p].y} stroke={corLinha} strokeWidth={6} strokeLinecap="round" opacity={0.8} />
        ))}
        {ult && dedo && <Line x1={ult.x} y1={ult.y} x2={dedo.x} y2={dedo.y} stroke={corLinha} strokeWidth={4} strokeLinecap="round" opacity={0.5} />}
        {centros.map((c, i) => {
          const on = seq.includes(i);
          return (
            <Circle key={i} cx={c.x} cy={c.y} r={on ? 14 : 9} fill={on ? corLinha : C.sub}
              stroke={on ? alpha(corLinha, 0.35) : 'transparent'} strokeWidth={on ? 14 : 0} />
          );
        })}
      </Svg>
      </View>
    </View>
  );
}
